import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { DATE_LOCALES } from "@/i18n";
import { Loading } from "@/components/common";

export default function BlogList() {
  const { t, i18n } = useTranslation();
  const [items, setItems] = useState(null);
  const dateLoc = DATE_LOCALES[i18n.language?.slice(0, 2)] || "en-IN";

  useEffect(() => {
    setItems(null);
    api.get("/public/blogs?limit=50")
      .then(({ data }) => setItems(data.items || []))
      .catch(() => setItems([]));
  }, [i18n.language]);

  if (!items) return <Loading label={t("blog.loadingList")} />;

  return (
    <div data-testid="blog-list-page">
      <section className="bg-navy text-ivory hero-grain relative py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-gold mb-4">{t("blog.kicker")}</p>
          <h1 className="font-serif text-4xl sm:text-6xl font-semibold tracking-tight">{t("blog.title")}</h1>
          <p className="mt-5 max-w-2xl text-ivory/70">{t("blog.intro")}</p>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {items.length === 0 ? (
            <p className="text-muted-foreground">{t("blog.empty")}</p>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {items.map((post) => (
                <article key={post.id}>
                  <Link to={`/blog/${post.slug}`} data-testid={`blog-card-${post.slug}`} className="group block">
                    <div className="overflow-hidden mb-5">
                      <img src={post.cover_image} alt={post.title}
                        className="w-full h-56 object-cover group-hover:scale-105 transition-transform duration-500" />
                    </div>
                    <p className="text-xs font-semibold uppercase tracking-widest text-gold-700">
                      {post.category} · {t("common.minRead", { count: post.read_time })}
                    </p>
                    <h2 className="font-serif text-2xl font-semibold text-navy mt-2 leading-snug group-hover:text-gold-700 transition-colors">
                      {post.title}
                    </h2>
                    <p className="text-sm text-muted-foreground mt-3 line-clamp-3">{post.excerpt}</p>
                    <p className="text-xs text-muted-foreground mt-4">{fmtDate(post.published_at, dateLoc)}</p>
                    <span className="inline-flex items-center gap-2 mt-4 text-xs font-semibold uppercase tracking-wider text-navy">
                      {t("blog.read")} <ArrowRight className="h-3.5 w-3.5" />
                    </span>
                  </Link>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
